const OPERATOR_GREATER = ">";
const OPERATOR_LESS = "<";
const OPERATOR_IS = "is";
const OPERATOR_NOT = "isnot";
const OPERATOR_CONTAINS = "contains";
const OPERATOR_STARTS_WITH = "starts_with";
const OPERATOR_ENDS_WITH = "ends_with";
const LOGIC_ALL = "all";
const LOGIC_ANY = "any";
const ACTION_SHOW = "show";
const ACTION_HIDE = "hide";

export const isset = value => typeof value !== "undefined" && value !== null;

export const match_value = (value, match) => {
    switch (true) {
        case Array.isArray(value):
            return (
                value.reduce(
                    (r, i) =>
                        i === Object(i)
                            ? match_object_values(i, match)
                                ? [...r, i]
                                : r
                            : match(i)
                            ? [...r, i]
                            : r,
                    []
                ).length > 0
            );
        case value === Object(value):
            return match_object_values(value, match);
        default:
            return match(isset(value) ? value : "");
    }
};

const match_object_values = (object, match) => {
    return (
        Object.values(object).reduce((r, i) => (match(i) ? [...r, i] : r), [])
            .length > 0
    );
};

export const evaluate_rule = (rule, input) => {
    const { operator, value } = rule;
    switch (operator) {
        case OPERATOR_GREATER:
            return match_value(input, i => Number(i) > Number(value));
        case OPERATOR_LESS:
            return match_value(input, i => Number(i) < Number(value));
        default:
        case OPERATOR_IS:
            return match_value(input, i => i === value);
        case OPERATOR_NOT:
            return !match_value(input, i => i === value);
        case OPERATOR_CONTAINS:
            return match_value(input, i =>
                i.toString().includes(value.toString())
            );
        case OPERATOR_STARTS_WITH:
            return match_value(input, i =>
                i.toString().startsWith(value.toString())
            );
        case OPERATOR_ENDS_WITH:
            return match_value(input, i =>
                i.toString().endsWith(value.toString())
            );
    }
};

const match_rules = ({ rules }, entry) =>
    rules.reduce((result, rule) => {
        const { name } = rule;
        if (evaluate_rule(rule, entry[name])) {
            result.push(rule);
        }
        return result;
    }, []);

/** Check dependency conditional logic */
const match_rulesV2 = ({ rules }, context) =>
    rules.reduce((result, rule) => {
        const { name } = rule;
        const [field, input] = `${name}`.split(".");

        const dependency = context.controlProps[field]
            ? context.controlProps[field]
            : {};
        const section =
            context.controlProps[field] &&
            context.controlProps[field]["section"]
                ? context.controlProps[field]["section"]
                : {};

        if (
            !evaluates_as_hiddenV2(section.conditionalLogic, context) &&
            !evaluates_as_hiddenV2(dependency.conditionalLogic, context) &&
            evaluate_rule(
                rule,
                input && context.values[field]
                    ? context.values[field][
                          `${field.replace("input_", "")}.${input}`
                      ]
                    : context.values[field]
            )
        ) {
            result.push(rule);
        }
        return result;
    }, []);

const evaluate_conditional_logic = (logic, entry) => {
    if (!logic) {
        return true;
    }
    const { logicType = LOGIC_ANY, rules } = logic;
    const matches = match_rules(logic, entry);
    return (
        (logicType === LOGIC_ALL && matches.length === rules.length) ||
        (logicType === LOGIC_ANY && matches.length > 0)
    );
};

const evaluate_conditional_logicV2 = (logic, context) => {
    if (!logic) {
        return true;
    }
    const { logicType = LOGIC_ANY, rules } = logic;
    const matches = match_rulesV2(logic, context);
    return (
        (logicType === LOGIC_ALL && matches.length === rules.length) ||
        (logicType === LOGIC_ANY && matches.length > 0)
    );
};

const evaluates_as_hidden = (logic, entry) => {
    if (!logic) {
        return false;
    }
    const { actionType = ACTION_HIDE } = logic;
    const valid = evaluate_conditional_logic(logic, entry);
    return (
        (valid && actionType === ACTION_HIDE) ||
        (!valid && actionType === ACTION_SHOW)
    );
};

const evaluates_as_hiddenV2 = (logic, context) => {
    if (!logic) {
        return false;
    }
    const { actionType = ACTION_HIDE } = logic;

    const valid = evaluate_conditional_logicV2(logic, context);
    return (
        (valid && actionType === ACTION_HIDE) ||
        (!valid && actionType === ACTION_SHOW)
    );
};

const evaluates_as_visible = (logic, entry) => {
    return !evaluates_as_hidden(logic, entry);
};

const evaluates_as_visibleV2 = (logic, context) => {
    return !evaluates_as_hiddenV2(logic, context);
};

export {
    evaluate_conditional_logic,
    evaluates_as_hidden,
    evaluates_as_visible,
    evaluates_as_hiddenV2,
    evaluates_as_visibleV2,
    match_rules
};
